// Staff adventure form view - create/edit adventures with maps and exit conditions

import { css, html } from 'lit';
import { api, getImageUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

/**
 * @typedef {object} AdventureFormData
 * @property {string} name
 * @property {string} slug
 * @property {string} description
 * @property {string} level
 * @property {string} players
 * @property {string} duration
 * @property {boolean} published
 * @property {string|null} capsule_image_asset_slug
 * @property {string|null} hook_text
 * @property {string|null} hook_image_asset_slug
 * @property {string|null} objective
 */

/**
 * @typedef {object} ValidationState
 * @property {boolean} showError
 * @property {boolean} [checking]
 * @property {boolean} [checked]
 * @property {boolean} [exists]
 */

/**
 * @typedef {object} ToastState
 * @property {boolean} show
 * @property {string} type
 * @property {string} message
 */

/**
 * @typedef {object} MapUploadData
 * @property {string} name
 * @property {number} gridSize
 * @property {File|null} imageFile
 */

/**
 * @typedef {object} MapRecord
 * @property {string} id
 * @property {string} name
 * @property {number} grid_size
 */

/**
 * @typedef {object} ExitConditionRecord
 * @property {string} id
 * @property {string} conditionType
 * @property {string} [mapName]
 * @property {string} [targetSlug]
 * @property {string} [targetTokenId]
 */

/**
 * @typedef {object} NewExitCondition
 * @property {string} conditionType
 * @property {string} mapId
 * @property {string} targetSlug
 * @property {string} targetTokenId
 */

/**
 * Staff adventure form view component for creating/editing adventures
 */
class StaffAdventureFormView extends BaseComponent {
  static properties = {
    slug: { type: String },
    _loading: { type: Boolean, state: true },
    _saving: { type: Boolean, state: true },
    _formData: { type: Object, state: true },
    _toast: { type: Object, state: true },
    // Validation states
    _nameValidation: { type: Object, state: true },
    _slugValidation: { type: Object, state: true },
    _levelValidation: { type: Object, state: true },
    _playersValidation: { type: Object, state: true },
    _durationValidation: { type: Object, state: true },
    _imageValidation: { type: Object, state: true },
    // Image states
    _isDragging: { type: Boolean, state: true },
    _imageFile: { type: Object, state: true },
    _imagePreview: { type: String, state: true },
    // Hook image states
    _isHookDragging: { type: Boolean, state: true },
    _hookImageFile: { type: Object, state: true },
    _hookImagePreview: { type: String, state: true },
    // Maps states
    _maps: { type: Array, state: true },
    _mapsLoading: { type: Boolean, state: true },
    _showMapUploadModal: { type: Boolean, state: true },
    _mapUploading: { type: Boolean, state: true },
    _isMapDragging: { type: Boolean, state: true },
    _mapImagePreview: { type: String, state: true },
    _mapUploadData: { type: Object, state: true },
    _mapZoom: { type: Number, state: true },
    _mapPan: { type: Object, state: true },
    _isPanning: { type: Boolean, state: true },
    // Exit conditions states
    _exitConditions: { type: Array, state: true },
    _exitConditionsLoading: { type: Boolean, state: true },
    _showAddExitConditionModal: { type: Boolean, state: true },
    _exitConditionSaving: { type: Boolean, state: true },
    _newExitCondition: { type: Object, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .toast-container {
      position: fixed;
      top: 0;
      right: 0;
      padding: 1rem;
      z-index: 9999;
    }

    .toast {
      min-width: 300px;
    }

    .drop-zone {
      cursor: pointer;
    }

    .modal-backdrop {
      background-color: rgba(0, 0, 0, 0.5);
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1050;
    }

    .map-modal .modal-dialog {
      max-width: calc(100vw - 290px);
      width: calc(100vw - 290px);
      margin: 20px 20px 20px 270px;
    }

    .map-modal .modal-content {
      height: calc(100vh - 40px);
    }

    .map-preview-container {
      position: relative;
      overflow: hidden;
      background-color: #f8f9fa;
    }

    .zoom-controls {
      position: absolute;
      top: 0;
      right: 0;
      margin: 1rem;
      z-index: 10;
    }
  `;

  constructor() {
    super();
    /** @type {string|null} */
    this.slug = null;
    this._loading = true;
    this._saving = false;
    /** @type {AdventureFormData} */
    this._formData = {
      name: '',
      slug: '',
      description: '',
      level: '',
      players: '',
      duration: '',
      published: false,
      capsule_image_asset_slug: null,
      hook_text: null,
      hook_image_asset_slug: null,
      objective: null,
    };
    /** @type {ToastState} */
    this._toast = { show: false, type: 'error', message: '' };
    /** @type {ValidationState} */
    this._nameValidation = { showError: false };
    /** @type {ValidationState} */
    this._slugValidation = { showError: false, checking: false, checked: false, exists: false };
    /** @type {ValidationState} */
    this._levelValidation = { showError: false };
    /** @type {ValidationState} */
    this._playersValidation = { showError: false };
    /** @type {ValidationState} */
    this._durationValidation = { showError: false };
    /** @type {ValidationState} */
    this._imageValidation = { showError: false };
    this._isDragging = false;
    /** @type {File|null} */
    this._imageFile = null;
    /** @type {string|null} */
    this._imagePreview = null;
    this._isHookDragging = false;
    /** @type {File|null} */
    this._hookImageFile = null;
    /** @type {string|null} */
    this._hookImagePreview = null;
    /** @type {Array<MapRecord>} */
    this._maps = [];
    this._mapsLoading = false;
    this._showMapUploadModal = false;
    this._mapUploading = false;
    this._isMapDragging = false;
    /** @type {string|null} */
    this._mapImagePreview = null;
    /** @type {MapUploadData} */
    this._mapUploadData = { name: '', gridSize: 70, imageFile: null };
    this._mapZoom = 1.0;
    /** @type {{x: number, y: number}} */
    this._mapPan = { x: 0, y: 0 };
    this._isPanning = false;
    /** @type {{x: number, y: number}} */
    this._panStart = { x: 0, y: 0 };
    /** @type {Array<ExitConditionRecord>} */
    this._exitConditions = [];
    this._exitConditionsLoading = false;
    this._showAddExitConditionModal = false;
    this._exitConditionSaving = false;
    /** @type {NewExitCondition} */
    this._newExitCondition = {
      conditionType: 'defeat-all-of-type',
      mapId: '',
      targetSlug: '',
      targetTokenId: '',
    };
    /** @type {ReturnType<typeof setTimeout>|null} */
    this._slugCheckTimeout = null;
  }

  get _isEditMode() {
    return !!this.slug;
  }

  async connectedCallback() {
    super.connectedCallback();
    if (this._isEditMode) {
      await this._loadAdventure();
      await this._loadMaps();
      await this._loadExitConditions();
    } else {
      this._loading = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._slugCheckTimeout) {
      clearTimeout(this._slugCheckTimeout);
    }
  }

  /**
   * @param {string} message
   * @param {string} [type]
   */
  _showToast(message, type = 'error') {
    this._toast = { show: true, type, message };
  }

  _dismissToast() {
    this._toast = { ...this._toast, show: false };
  }

  async _loadAdventure() {
    try {
      this._loading = true;
      const data = await api.get(`/api/adventures/${this.slug}`);

      this._formData = {
        name: data.name,
        slug: data.slug,
        description: data.description || '',
        level: data.level || '',
        players: data.players || '',
        duration: data.duration || '',
        published: data.published,
        capsule_image_asset_slug: data.capsule_image_asset_slug || null,
        hook_text: data.hook_text || null,
        hook_image_asset_slug: data.hook_image_asset_slug || null,
        objective: data.objective || null,
      };

      if (data.capsule_image_url) {
        this._imagePreview = getImageUrl(data.capsule_image_url);
      }

      if (data.hook_image_url) {
        this._hookImagePreview = getImageUrl(data.hook_image_url);
      }
    } catch (err) {
      console.error('Error loading adventure:', err);
      if (err instanceof Error) {
        this._showToast(err.message);
      }
    } finally {
      this._loading = false;
    }
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  _slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /** @param {Event} e */
  _handleNameInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._formData = { ...this._formData, name: target.value };
    this._nameValidation = { showError: false };
    if (!this._isEditMode) {
      this._formData = { ...this._formData, slug: this._slugify(target.value) };
      this._checkSlugAvailability();
    }
  }

  /** @param {Event} e */
  _handleSlugInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._formData = { ...this._formData, slug: target.value };
    this._slugValidation = { ...this._slugValidation, showError: false };
    this._checkSlugAvailability();
  }

  async _checkSlugAvailability() {
    if (this._isEditMode || !this._formData.slug) {
      return;
    }

    if (this._slugCheckTimeout) {
      clearTimeout(this._slugCheckTimeout);
    }

    this._slugValidation = { ...this._slugValidation, checking: true, checked: false };

    this._slugCheckTimeout = setTimeout(async () => {
      try {
        const data = await api.get(`/api/adventures/check-slug/${this._formData.slug}`);
        this._slugValidation = {
          ...this._slugValidation,
          exists: data.exists,
          checked: true,
          checking: false,
        };
      } catch (err) {
        console.error('Error checking slug:', err);
        this._slugValidation = { ...this._slugValidation, checked: false, checking: false };
      }
    }, 500);
  }

  /** @param {Event} e */
  _handleDescriptionInput(e) {
    const target = /** @type {HTMLTextAreaElement} */ (e.target);
    this._formData = { ...this._formData, description: target.value };
  }

  /** @param {Event} e */
  _handleHookTextInput(e) {
    const target = /** @type {HTMLTextAreaElement} */ (e.target);
    this._formData = { ...this._formData, hook_text: target.value || null };
  }

  /** @param {Event} e */
  _handleObjectiveInput(e) {
    const target = /** @type {HTMLTextAreaElement} */ (e.target);
    this._formData = { ...this._formData, objective: target.value || null };
  }

  /** @param {Event} e */
  _handleLevelInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._formData = { ...this._formData, level: target.value };
    this._levelValidation = { showError: false };
  }

  /** @param {Event} e */
  _handlePlayersInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._formData = { ...this._formData, players: target.value };
    this._playersValidation = { showError: false };
  }

  /** @param {Event} e */
  _handleDurationInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._formData = { ...this._formData, duration: target.value };
    this._durationValidation = { showError: false };
  }

  /** @param {Event} e */
  _handlePublishedChange(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._formData = { ...this._formData, published: target.checked };
  }

  // Capsule image handlers
  _triggerFileInput() {
    const el = /** @type {HTMLInputElement|null} */ (this.shadowRoot?.querySelector('#capsuleFileInput'));
    el?.click();
  }

  /** @param {DragEvent} e */
  _handleDragOver(e) {
    e.preventDefault();
    this._isDragging = true;
  }

  /** @param {DragEvent} e */
  _handleDragLeave(e) {
    e.preventDefault();
    this._isDragging = false;
  }

  /** @param {DragEvent} e */
  _handleDrop(e) {
    e.preventDefault();
    this._isDragging = false;
    const files = e.dataTransfer?.files;
    const file = files?.[0];
    if (file) {
      this._handleFile(file);
    }
  }

  /** @param {Event} e */
  _handleFileSelect(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    const file = target.files?.[0];
    if (file) {
      this._handleFile(file);
    }
  }

  /** @param {File} file */
  _handleFile(file) {
    this._imageValidation = { showError: false };

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      this._showToast('Invalid file type. Please use JPEG, PNG, or WEBP.');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this._showToast('File too large. Maximum size is 10MB.');
      return;
    }

    this._imageFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this._imagePreview = /** @type {string} */ (e.target?.result);
    };
    reader.readAsDataURL(file);
  }

  /** @param {Event} e */
  _clearImage(e) {
    e.stopPropagation();
    this._imageFile = null;
    this._imagePreview = null;
    this._formData = { ...this._formData, capsule_image_asset_slug: null };
  }

  // Hook image handlers
  _triggerHookFileInput() {
    const el = /** @type {HTMLInputElement|null} */ (this.shadowRoot?.querySelector('#hookFileInput'));
    el?.click();
  }

  /** @param {DragEvent} e */
  _handleHookDragOver(e) {
    e.preventDefault();
    this._isHookDragging = true;
  }

  /** @param {DragEvent} e */
  _handleHookDragLeave(e) {
    e.preventDefault();
    this._isHookDragging = false;
  }

  /** @param {DragEvent} e */
  _handleHookDrop(e) {
    e.preventDefault();
    this._isHookDragging = false;
    const files = e.dataTransfer?.files;
    const file = files?.[0];
    if (file) {
      this._handleHookFile(file);
    }
  }

  /** @param {Event} e */
  _handleHookFileSelect(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    const file = target.files?.[0];
    if (file) {
      this._handleHookFile(file);
    }
  }

  /** @param {File} file */
  _handleHookFile(file) {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      this._showToast('Invalid file type. Please use JPEG, PNG, or WEBP.');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this._showToast('File too large. Maximum size is 10MB.');
      return;
    }

    this._hookImageFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this._hookImagePreview = /** @type {string} */ (e.target?.result);
    };
    reader.readAsDataURL(file);
  }

  /** @param {Event} e */
  _clearHookImage(e) {
    e.stopPropagation();
    this._hookImageFile = null;
    this._hookImagePreview = null;
    this._formData = { ...this._formData, hook_image_asset_slug: null };
  }

  // Upload helpers
  /**
   * Upload an image to d3-assets and create an asset. Returns the asset slug.
   * @param {File} file
   * @param {string} assetType - e.g., 'capsule', 'hook'
   * @param {string} slug - the asset slug
   * @returns {Promise<string>} the asset slug
   */
  async _uploadImageToD3Assets(file, assetType, slug) {
    // 1. Compute SHA-256 hash
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const contentHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    // 2. Initiate upload
    const initResult = await api.post('/api/d3-assets/uploads/initiate', {
      original_name: file.name,
      content_type: file.type,
      content_hash: contentHash,
    });

    // 3. Upload file if not deduplicated
    if (initResult.status !== 'completed') {
      await fetch(initResult.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      await this._waitForUploadCompletion(initResult.upload_id);
    }

    // 4. Create asset in d3-assets
    const formData = new FormData();
    formData.append('slug', slug);
    formData.append('asset_type', assetType);
    formData.append('source', 'd3');
    formData.append('upload_id', initResult.upload_id);
    const assetResult = await api.upload('/api/d3-assets/assets', formData);

    return assetResult.slug;
  }

  // Maps methods
  async _loadMaps() {
    if (!this.slug) return;

    try {
      this._mapsLoading = true;
      this._maps = await api.get(`/api/adventures/${this.slug}/maps`);
    } catch (err) {
      console.error('Error loading maps:', err);
    } finally {
      this._mapsLoading = false;
    }
  }

  _showUploadMapModal() {
    this._mapUploadData = { name: '', gridSize: 70, imageFile: null };
    this._mapImagePreview = null;
    this._mapZoom = 1.0;
    this._mapPan = { x: 0, y: 0 };
    this._showMapUploadModal = true;
  }

  _closeUploadMapModal() {
    this._showMapUploadModal = false;
    this._mapUploadData = { name: '', gridSize: 70, imageFile: null };
    this._mapImagePreview = null;
    this._isMapDragging = false;
    this._mapZoom = 1.0;
    this._mapPan = { x: 0, y: 0 };
    this._isPanning = false;
  }

  /** @param {Event} e */
  _handleMapNameInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._mapUploadData = { ...this._mapUploadData, name: target.value };
  }

  /** @param {Event} e */
  _handleGridSizeInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._mapUploadData = { ...this._mapUploadData, gridSize: parseInt(target.value, 10) || 70 };
    this._drawMapPreview();
  }

  /** @param {DragEvent} e */
  _handleMapDragOver(e) {
    e.preventDefault();
    this._isMapDragging = true;
  }

  /** @param {DragEvent} e */
  _handleMapDragLeave(e) {
    e.preventDefault();
    this._isMapDragging = false;
  }

  /** @param {DragEvent} e */
  _handleMapDrop(e) {
    e.preventDefault();
    this._isMapDragging = false;
    const files = e.dataTransfer?.files;
    const file = files?.[0];
    if (file) {
      this._handleMapFile(file);
    }
  }

  /** @param {File} file */
  _handleMapFile(file) {
    this._mapUploadData = { ...this._mapUploadData, imageFile: file };

    const reader = new FileReader();
    reader.onload = (e) => {
      this._mapImagePreview = /** @type {string} */ (e.target?.result);
      requestAnimationFrame(() => this._drawMapPreview());
    };
    reader.readAsDataURL(file);
  }

  _clearMapImage() {
    this._mapUploadData = { ...this._mapUploadData, imageFile: null };
    this._mapImagePreview = null;
  }

  _drawMapPreview() {
    const canvas = /** @type {HTMLCanvasElement|null} */ (this.shadowRoot?.querySelector('#mapPreviewCanvas'));
    if (!canvas || !this._mapImagePreview) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      ctx.drawImage(img, 0, 0);

      const gridSize = this._mapUploadData.gridSize;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.lineWidth = 1;

      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }

      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    };
    img.src = this._mapImagePreview;
  }

  _zoomIn() {
    this._mapZoom = Math.min(this._mapZoom + 0.25, 3.0);
  }

  _zoomOut() {
    this._mapZoom = Math.max(this._mapZoom - 0.25, 0.25);
  }

  _resetZoom() {
    this._mapZoom = 1.0;
    this._mapPan = { x: 0, y: 0 };
  }

  /** @param {MouseEvent} e */
  _startPan(e) {
    this._isPanning = true;
    this._panStart = {
      x: e.clientX - this._mapPan.x,
      y: e.clientY - this._mapPan.y,
    };
  }

  /** @param {MouseEvent} e */
  _doPan(e) {
    if (!this._isPanning) return;

    const canvas = /** @type {HTMLCanvasElement|null} */ (this.shadowRoot?.querySelector('#mapPreviewCanvas'));
    const container = canvas?.parentElement;
    if (!canvas || !container) return;

    const newX = e.clientX - this._panStart.x;
    const newY = e.clientY - this._panStart.y;

    const scaledWidth = canvas.width * this._mapZoom;
    const scaledHeight = canvas.height * this._mapZoom;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    let maxX = 0,
      minX = 0,
      maxY = 0,
      minY = 0;

    if (scaledWidth > containerWidth) {
      const overflow = (scaledWidth - containerWidth) / 2;
      maxX = overflow;
      minX = -overflow;
    }

    if (scaledHeight > containerHeight) {
      const overflow = (scaledHeight - containerHeight) / 2;
      maxY = overflow;
      minY = -overflow;
    }

    this._mapPan = {
      x: Math.max(minX, Math.min(maxX, newX)),
      y: Math.max(minY, Math.min(maxY, newY)),
    };
  }

  _endPan() {
    this._isPanning = false;
  }

  async _uploadMap() {
    try {
      this._mapUploading = true;

      if (!this._mapUploadData.name) {
        this._showToast('Please enter a map name');
        return;
      }

      if (!this._mapUploadData.imageFile) {
        this._showToast('Please select a map image');
        return;
      }

      const file = this._mapUploadData.imageFile;

      // 1. Compute SHA-256 hash of the file
      const arrayBuffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const contentHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

      // 2. Initiate upload via d3-assets proxy
      const initResult = await api.post('/api/d3-assets/uploads/initiate', {
        original_name: file.name,
        content_type: file.type,
        content_hash: contentHash,
      });

      // 3. Upload file if not already deduplicated
      if (initResult.status !== 'completed') {
        await fetch(initResult.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        // 4. Poll for upload completion
        await this._waitForUploadCompletion(initResult.upload_id);
      }

      // 5. Create tactical-map asset in d3-assets via proxy
      const slug = this._mapUploadData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const formData = new FormData();
      formData.append('slug', slug);
      formData.append('asset_type', 'tactical-map');
      formData.append('source', 'd3');
      formData.append('grid_dpi', String(this._mapUploadData.gridSize));
      formData.append('upload_id', initResult.upload_id);
      const assetResult = await api.upload('/api/d3-assets/assets', formData);

      // 6. Get grid dimensions from asset response
      const gridRows = assetResult.current_file?.height
        ? Math.floor(assetResult.current_file.height / this._mapUploadData.gridSize)
        : undefined;
      const gridCols = assetResult.current_file?.width
        ? Math.floor(assetResult.current_file.width / this._mapUploadData.gridSize)
        : undefined;

      // 7. Create map in rpgish
      await api.post(`/api/adventures/${this.slug}/maps`, {
        name: this._mapUploadData.name,
        image_asset_slug: assetResult.slug,
        grid_size: this._mapUploadData.gridSize,
        grid_rows: gridRows,
        grid_cols: gridCols,
      });

      await this._loadMaps();
      this._closeUploadMapModal();
    } catch (err) {
      console.error('Error uploading map:', err);
      if (err instanceof Error) {
        this._showToast(err.message);
      }
    } finally {
      this._mapUploading = false;
    }
  }

  /**
   * Polls upload status until completed or failed.
   * @param {string} uploadId
   */
  async _waitForUploadCompletion(uploadId) {
    const maxAttempts = 60;
    for (let i = 0; i < maxAttempts; i++) {
      const status = await api.get(`/api/d3-assets/uploads/${uploadId}/status`);
      if (status.status === 'completed') return;
      if (status.status === 'failed') throw new Error('Upload processing failed');
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    throw new Error('Upload processing timed out');
  }

  /** @param {string} mapId */
  async _deleteMap(mapId) {
    if (!confirm('Are you sure you want to delete this map?')) {
      return;
    }

    try {
      await api.delete(`/api/adventures/${this.slug}/maps/${mapId}`);
      await this._loadMaps();
    } catch (err) {
      console.error('Error deleting map:', err);
      if (err instanceof Error) {
        this._showToast(err.message);
      }
    }
  }

  /** @param {string} mapId */
  _editMap(mapId) {
    router.navigate(`/staff/adventures/${this.slug}/maps/${mapId}/edit`);
  }

  // Exit conditions methods
  async _loadExitConditions() {
    if (!this.slug) return;

    try {
      this._exitConditionsLoading = true;
      this._exitConditions = (await api.get(`/api/adventures/${this.slug}/exit-conditions`)) || [];
    } catch (err) {
      console.error('Error loading exit conditions:', err);
    } finally {
      this._exitConditionsLoading = false;
    }
  }

  _openAddExitConditionModal() {
    this._newExitCondition = {
      conditionType: 'defeat-all-of-type',
      mapId: this._maps[0]?.id ?? '',
      targetSlug: '',
      targetTokenId: '',
    };
    this._showAddExitConditionModal = true;
  }

  _closeAddExitConditionModal() {
    this._showAddExitConditionModal = false;
  }

  /** @param {Event} e */
  _handleConditionTypeChange(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._newExitCondition = { ...this._newExitCondition, conditionType: target.value };
  }

  /** @param {Event} e */
  _handleConditionMapChange(e) {
    const target = /** @type {HTMLSelectElement} */ (e.target);
    this._newExitCondition = { ...this._newExitCondition, mapId: target.value };
  }

  /** @param {Event} e */
  _handleTargetSlugInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._newExitCondition = { ...this._newExitCondition, targetSlug: target.value };
  }

  /** @param {Event} e */
  _handleTargetTokenIdInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._newExitCondition = { ...this._newExitCondition, targetTokenId: target.value };
  }

  async _createExitCondition() {
    try {
      this._exitConditionSaving = true;

      /** @type {{conditionType: string, mapId: string, targetSlug?: string, targetTokenId?: string}} */
      const payload = {
        conditionType: this._newExitCondition.conditionType,
        mapId: this._newExitCondition.mapId,
      };

      if (this._newExitCondition.conditionType === 'defeat-all-of-type') {
        if (!this._newExitCondition.targetSlug) {
          this._showToast('Target slug is required');
          return;
        }
        payload.targetSlug = this._newExitCondition.targetSlug;
      } else {
        if (!this._newExitCondition.targetTokenId) {
          this._showToast('Target token ID is required');
          return;
        }
        payload.targetTokenId = this._newExitCondition.targetTokenId;
      }

      await api.post(`/api/adventures/${this.slug}/exit-conditions`, payload);
      await this._loadExitConditions();
      this._closeAddExitConditionModal();
    } catch (err) {
      console.error('Error creating exit condition:', err);
      if (err instanceof Error) {
        this._showToast(err.message);
      }
    } finally {
      this._exitConditionSaving = false;
    }
  }

  /** @param {string} conditionId */
  async _deleteExitCondition(conditionId) {
    if (!confirm('Are you sure you want to delete this exit condition?')) {
      return;
    }

    try {
      await api.delete(`/api/adventures/${this.slug}/exit-conditions/${conditionId}`);
      await this._loadExitConditions();
    } catch (err) {
      console.error('Error deleting exit condition:', err);
      if (err instanceof Error) {
        this._showToast(err.message);
      }
    }
  }

  /** @param {ExitConditionRecord} condition */
  _formatExitCondition(condition) {
    if (condition.conditionType === 'defeat-all-of-type') {
      return `Defeat all "${condition.targetSlug}" on ${condition.mapName || 'map'}`;
    } else {
      return `Defeat specific token on ${condition.mapName || 'map'}`;
    }
  }

  // Save adventure
  /** @param {Event} e */
  async _saveAdventure(e) {
    e.preventDefault();

    try {
      this._saving = true;

      // Reset validations
      this._nameValidation = { showError: false };
      this._slugValidation = { ...this._slugValidation, showError: false };
      this._levelValidation = { showError: false };
      this._playersValidation = { showError: false };
      this._durationValidation = { showError: false };
      this._imageValidation = { showError: false };

      let hasErrors = false;

      if (!this._formData.name) {
        this._nameValidation = { showError: true };
        hasErrors = true;
      }

      if (!this._formData.slug) {
        this._slugValidation = { ...this._slugValidation, showError: true };
        hasErrors = true;
      }

      if (!this._formData.level) {
        this._levelValidation = { showError: true };
        hasErrors = true;
      }

      if (!this._formData.players) {
        this._playersValidation = { showError: true };
        hasErrors = true;
      }

      if (!this._formData.duration) {
        this._durationValidation = { showError: true };
        hasErrors = true;
      }

      if (!this._formData.capsule_image_asset_slug && !this._imageFile) {
        this._imageValidation = { showError: true };
        hasErrors = true;
      }

      if (hasErrors) {
        this._saving = false;
        return;
      }

      if (!this._isEditMode && this._slugValidation.exists) {
        this._showToast('Please choose a different slug. This one is already taken.');
        this._saving = false;
        return;
      }

      // Upload capsule image to d3-assets if new file selected
      let capsuleSlug = this._formData.capsule_image_asset_slug;
      if (this._imageFile) {
        const capsuleAssetSlug = `capsule-${this._formData.slug}`;
        capsuleSlug = await this._uploadImageToD3Assets(this._imageFile, 'capsule', capsuleAssetSlug);
      }

      // Upload hook image to d3-assets if new file selected
      let hookSlug = this._formData.hook_image_asset_slug;
      if (this._hookImageFile) {
        const hookAssetSlug = `hook-${this._formData.slug}`;
        hookSlug = await this._uploadImageToD3Assets(this._hookImageFile, 'hook', hookAssetSlug);
      }

      const payload = {
        name: this._formData.name,
        slug: this._formData.slug,
        description: this._formData.description,
        level: this._formData.level,
        players: this._formData.players,
        duration: this._formData.duration,
        published: this._formData.published,
        capsule_image_asset_slug: capsuleSlug,
        hook_text: this._formData.hook_text || null,
        hook_image_asset_slug: hookSlug,
        objective: this._formData.objective || null,
        maps: [],
      };

      if (this._isEditMode) {
        await api.put(`/api/adventures/${this.slug}`, payload);
      } else {
        await api.post('/api/adventures', payload);
      }

      router.navigate('/staff/adventures');
    } catch (err) {
      console.error('Error saving adventure:', err);
      if (err instanceof Error) {
        this._showToast(err.message);
      }
    } finally {
      this._saving = false;
    }
  }

  _goBack() {
    router.navigate('/staff/adventures');
  }

  render() {
    return html`
      <div class="container-fluid p-4">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">Staff</li>
            <li class="breadcrumb-item">
              <a
                href="#"
                @click="${
                  /** @param {Event} e */ (e) => {
                    e.preventDefault();
                    this._goBack();
                  }
                }"
                >Adventures</a
              >
            </li>
            <li class="breadcrumb-item active" aria-current="page">${this._isEditMode ? 'Edit' : 'New'}</li>
          </ol>
        </nav>

        <h2 class="mb-4">${this._isEditMode ? 'Edit Adventure' : 'New Adventure'}</h2>

        ${this._renderToast()}
        ${
          this._loading
            ? html`
              <div class="text-center py-4">
                <div class="spinner-border">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            `
            : this._renderForm()
        }
        ${this._showMapUploadModal ? this._renderMapUploadModal() : ''}
        ${this._showAddExitConditionModal ? this._renderAddExitConditionModal() : ''}
      </div>
    `;
  }

  _renderToast() {
    if (!this._toast.show) return '';

    return html`
      <div class="toast-container">
        <div class="toast show ${this._toast.type === 'error' ? 'bg-danger' : 'bg-success'}" role="alert">
          <div class="toast-header ${this._toast.type === 'error' ? 'bg-danger' : 'bg-success'}">
            <strong class="me-auto text-white"> ${this._toast.type === 'error' ? 'Error' : 'Success'} </strong>
            <button type="button" class="btn-close btn-close-white" @click="${this._dismissToast}"></button>
          </div>
          <div class="toast-body text-white">${this._toast.message}</div>
        </div>
      </div>
    `;
  }

  _renderForm() {
    return html`
      <form novalidate style="max-width: 800px;" @submit="${this._saveAdventure}">
        <div class="card">
          <div class="card-body">
            ${this._renderNameField()} ${this._renderSlugField()} ${this._renderDescriptionField()}
            ${this._renderCapsuleImageField()} ${this._renderHookSection()} ${this._renderObjectiveField()}
            ${this._renderLevelPlayersFields()} ${this._renderMapsSection()}
            ${this._isEditMode ? this._renderExitConditionsSection() : ''}
            ${this._isEditMode ? this._renderPublishedField() : ''}
          </div>
          <div class="card-footer bg-white">
            <div class="d-flex gap-2 justify-content-end">
              <a
                href="#"
                class="btn btn-outline-secondary"
                @click="${
                  /** @param {Event} e */ (e) => {
                    e.preventDefault();
                    this._goBack();
                  }
                }"
              >
                Cancel
              </a>
              <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                ${
                  this._saving
                    ? html`
                      <span class="spinner-border spinner-border-sm me-1"></span>
                      Saving...
                    `
                    : 'Save Adventure'
                }
              </button>
            </div>
          </div>
        </div>
      </form>
    `;
  }

  _renderNameField() {
    return html`
      <div class="mb-3">
        <label for="adventureName" class="form-label">Name</label>
        <input
          id="adventureName"
          type="text"
          class="form-control ${this._nameValidation.showError && !this._formData.name ? 'is-invalid' : ''}"
          .value="${this._formData.name}"
          @input="${this._handleNameInput}"
          required
        />
        ${
          this._nameValidation.showError && !this._formData.name
            ? html` <div class="invalid-feedback">Name is required.</div> `
            : ''
        }
      </div>
    `;
  }

  _renderSlugField() {
    const showValid =
      !this._isEditMode && this._slugValidation.checked && !this._slugValidation.exists && this._formData.slug;
    const showInvalid =
      (!this._isEditMode && this._slugValidation.checked && this._slugValidation.exists) ||
      (this._slugValidation.showError && !this._formData.slug);

    return html`
      <div class="mb-3">
        <label for="adventureSlug" class="form-label">Slug</label>
        <input
          id="adventureSlug"
          type="text"
          class="form-control ${showValid ? 'is-valid' : ''} ${showInvalid ? 'is-invalid' : ''}"
          .value="${this._formData.slug}"
          @input="${this._handleSlugInput}"
          pattern="[a-z0-9]+(-[a-z0-9]+)*"
          required
        />
        ${
          this._slugValidation.checking
            ? html` <small class="form-text text-muted">Checking availability...</small> `
            : this._slugValidation.showError && !this._formData.slug
              ? html` <div class="invalid-feedback">Slug is required.</div> `
              : !this._isEditMode && this._slugValidation.checked && this._slugValidation.exists
                ? html` <div class="invalid-feedback">This slug is already taken. Please choose a different one.</div> `
                : !this._isEditMode && this._slugValidation.checked && !this._slugValidation.exists
                  ? html` <div class="valid-feedback">Slug is available!</div> `
                  : html`
                    <small class="form-text text-muted"
                      >Lowercase letters, numbers, and hyphens only. Auto-generated from name.</small
                    >
                  `
        }
      </div>
    `;
  }

  _renderDescriptionField() {
    return html`
      <div class="mb-3">
        <label for="adventureDescription" class="form-label">Description</label>
        <textarea
          id="adventureDescription"
          class="form-control"
          rows="3"
          .value="${this._formData.description}"
          @input="${this._handleDescriptionInput}"
        ></textarea>
      </div>
    `;
  }

  _renderCapsuleImageField() {
    return html`
      <div class="mb-3">
        <label class="form-label">Capsule Image</label>
        <div
          class="border rounded p-4 text-center drop-zone ${this._isDragging ? 'border-primary bg-light' : ''} ${
            this._imageValidation.showError && !this._imagePreview ? 'border-danger' : ''
          }"
          @dragover="${this._handleDragOver}"
          @dragleave="${this._handleDragLeave}"
          @drop="${this._handleDrop}"
          @click="${this._triggerFileInput}"
        >
          ${
            this._imagePreview
              ? html`
                <div>
                  <img src="${this._imagePreview}" alt="Preview" class="img-fluid mb-2" style="max-height: 200px;" />
                  <div>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="${this._clearImage}">
                      Remove Image
                    </button>
                  </div>
                </div>
              `
              : html`
                <div>
                  <i class="bi bi-cloud-upload fs-1 text-muted"></i>
                  <p class="mb-0">Drag and drop an image here, or click to select</p>
                  <small class="text-muted">JPEG, PNG, or WEBP (max 10MB)</small>
                </div>
              `
          }
          <input
            id="capsuleFileInput"
            type="file"
            class="d-none"
            accept="image/jpeg,image/png,image/webp"
            @change="${this._handleFileSelect}"
          />
        </div>
        ${
          this._imageValidation.showError && !this._imagePreview
            ? html` <div class="invalid-feedback d-block">Capsule image is required. Please upload an image.</div> `
            : ''
        }
      </div>
    `;
  }

  _renderHookSection() {
    return html`
      <div class="mb-3">
        <label for="hookText" class="form-label">Adventure Hook</label>
        <textarea
          id="hookText"
          class="form-control"
          rows="4"
          placeholder="Introduction text shown to players when they start the adventure... (supports Markdown)"
          .value="${this._formData.hook_text || ''}"
          @input="${this._handleHookTextInput}"
        ></textarea>
        <small class="form-text text-muted">
          This text is displayed as a modal overlay when players first start the game, before PC placement.
        </small>
      </div>

      <div class="mb-3">
        <label class="form-label">Hook Image (optional)</label>
        <div
          class="border rounded p-4 text-center drop-zone ${this._isHookDragging ? 'border-primary bg-light' : ''}"
          @dragover="${this._handleHookDragOver}"
          @dragleave="${this._handleHookDragLeave}"
          @drop="${this._handleHookDrop}"
          @click="${this._triggerHookFileInput}"
        >
          ${
            this._hookImagePreview
              ? html`
                <div>
                  <img
                    src="${this._hookImagePreview}"
                    alt="Hook Image Preview"
                    class="img-fluid mb-2"
                    style="max-height: 200px;"
                  />
                  <div>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="${this._clearHookImage}">
                      Remove Image
                    </button>
                  </div>
                </div>
              `
              : html`
                <div>
                  <i class="bi bi-cloud-upload fs-1 text-muted"></i>
                  <p class="mb-0">Drag and drop an image here, or click to select</p>
                  <small class="text-muted">JPEG, PNG, or WEBP (max 10MB)</small>
                </div>
              `
          }
          <input
            id="hookFileInput"
            type="file"
            class="d-none"
            accept="image/jpeg,image/png,image/webp"
            @change="${this._handleHookFileSelect}"
          />
        </div>
        <small class="form-text text-muted"> Optional image displayed alongside the hook text. </small>
      </div>
    `;
  }

  _renderObjectiveField() {
    return html`
      <div class="mb-3">
        <label for="objective" class="form-label">Objective (Optional)</label>
        <textarea
          id="objective"
          class="form-control"
          rows="2"
          placeholder="Describe the goal of this adventure..."
          .value="${this._formData.objective || ''}"
          @input="${this._handleObjectiveInput}"
        ></textarea>
        <small class="form-text text-muted">
          Displayed during PC placement to help players understand their goal.
        </small>
      </div>
    `;
  }

  _renderLevelPlayersFields() {
    return html`
      <div class="row">
        <div class="col-md-4 mb-3">
          <label for="adventureLevel" class="form-label">Level</label>
          <input
            id="adventureLevel"
            type="text"
            class="form-control ${this._levelValidation.showError && !this._formData.level ? 'is-invalid' : ''}"
            placeholder="e.g., 1-5"
            .value="${this._formData.level}"
            @input="${this._handleLevelInput}"
            required
          />
          ${
            this._levelValidation.showError && !this._formData.level
              ? html` <div class="invalid-feedback">Level is required.</div> `
              : ''
          }
        </div>

        <div class="col-md-4 mb-3">
          <label for="adventurePlayers" class="form-label">Players</label>
          <input
            id="adventurePlayers"
            type="text"
            class="form-control ${this._playersValidation.showError && !this._formData.players ? 'is-invalid' : ''}"
            placeholder="e.g., 4-6"
            .value="${this._formData.players}"
            @input="${this._handlePlayersInput}"
            required
          />
          ${
            this._playersValidation.showError && !this._formData.players
              ? html` <div class="invalid-feedback">Players is required.</div> `
              : ''
          }
        </div>

        <div class="col-md-4 mb-3">
          <label for="adventureDuration" class="form-label">Duration</label>
          <input
            id="adventureDuration"
            type="text"
            class="form-control ${this._durationValidation.showError && !this._formData.duration ? 'is-invalid' : ''}"
            placeholder="e.g., 2-4 hours"
            .value="${this._formData.duration}"
            @input="${this._handleDurationInput}"
            required
          />
          ${
            this._durationValidation.showError && !this._formData.duration
              ? html` <div class="invalid-feedback">Duration is required.</div> `
              : ''
          }
        </div>
      </div>
    `;
  }

  _renderMapsSection() {
    if (!this._isEditMode) {
      return html`
        <div class="mb-3">
          <label class="form-label">Maps</label>
          <div class="alert alert-info">
            <i class="bi bi-info-circle"></i> Save the adventure first, then you can upload and edit maps.
          </div>
        </div>
      `;
    }

    return html`
      <div class="mb-3">
        <label class="form-label">Maps</label>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <button type="button" class="btn btn-sm btn-outline-primary" @click="${this._showUploadMapModal}">
            <i class="bi bi-plus-circle"></i> Upload Map
          </button>
        </div>

        ${
          this._mapsLoading
            ? html`
              <div class="text-center py-3">
                <div class="spinner-border spinner-border-sm">
                  <span class="visually-hidden">Loading maps...</span>
                </div>
              </div>
            `
            : this._maps.length === 0
              ? html` <div class="text-muted">No maps uploaded yet. Click "Upload Map" to add a map.</div> `
              : html`
                <div class="list-group">
                  ${this._maps.map(
                    (map) => html`
                      <div class="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>${map.name}</strong>
                          <small class="text-muted ms-2">Grid: ${map.grid_size}px</small>
                        </div>
                        <div class="btn-group btn-group-sm">
                          <button type="button" class="btn btn-outline-primary" @click="${() => this._editMap(map.id)}">
                            <i class="bi bi-pencil"></i> Edit Map
                          </button>
                          <button
                            type="button"
                            class="btn btn-outline-danger"
                            @click="${() => this._deleteMap(map.id)}"
                          >
                            <i class="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    `,
                  )}
                </div>
              `
        }

        <small class="form-text text-muted d-block mt-2"> Maps can only be added after creating the adventure. </small>
      </div>
    `;
  }

  _renderExitConditionsSection() {
    return html`
      <div class="mb-3">
        <label class="form-label">Exit Conditions</label>
        <small class="form-text text-muted d-block mb-2">
          At least one required to publish. All conditions must be met to complete adventure.
        </small>

        ${
          this._exitConditionsLoading
            ? html`
              <div class="text-center py-3">
                <div class="spinner-border spinner-border-sm">
                  <span class="visually-hidden">Loading exit conditions...</span>
                </div>
              </div>
            `
            : this._exitConditions.length === 0
              ? html` <div class="text-muted mb-2">No exit conditions defined yet.</div> `
              : html`
                <div class="list-group mb-2">
                  ${this._exitConditions.map(
                    (condition) => html`
                      <div class="list-group-item d-flex justify-content-between align-items-center">
                        <span>${this._formatExitCondition(condition)}</span>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          @click="${() => this._deleteExitCondition(condition.id)}"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    `,
                  )}
                </div>
              `
        }

        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          ?disabled="${this._maps.length === 0}"
          @click="${this._openAddExitConditionModal}"
        >
          <i class="bi bi-plus-circle"></i> Add Exit Condition
        </button>
        ${
          this._maps.length === 0
            ? html`
              <small class="form-text text-muted d-block mt-1"> Add a map first to define exit conditions. </small>
            `
            : ''
        }
      </div>
    `;
  }

  _renderPublishedField() {
    return html`
      <div class="mb-3">
        <div class="form-check">
          <input
            id="adventurePublished"
            class="form-check-input"
            type="checkbox"
            .checked="${this._formData.published}"
            @change="${this._handlePublishedChange}"
          />
          <label class="form-check-label" for="adventurePublished"> Published </label>
        </div>
        <small class="form-text text-muted">
          Adventure must have at least one map with 6 PC starting positions and at least one exit condition to be
          published.
        </small>
      </div>
    `;
  }

  _renderMapUploadModal() {
    return html`
      <div class="modal d-block modal-backdrop map-modal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Upload Map</h5>
              <button type="button" class="btn-close" @click="${this._closeUploadMapModal}"></button>
            </div>
            <div class="modal-body d-flex" style="overflow: hidden;">
              <!-- Left Panel - Form Controls -->
              <div class="pe-3" style="width: 300px; flex-shrink: 0;">
                <div class="mb-3">
                  <label for="mapName" class="form-label">Map Name</label>
                  <input
                    id="mapName"
                    type="text"
                    class="form-control"
                    placeholder="e.g., First Floor, Dungeon Level 1"
                    .value="${this._mapUploadData.name}"
                    @input="${this._handleMapNameInput}"
                  />
                </div>

                <div class="mb-3">
                  <label for="mapGridSize" class="form-label">Grid Size (pixels)</label>
                  <input
                    id="mapGridSize"
                    type="number"
                    class="form-control"
                    min="10"
                    max="200"
                    .value="${this._mapUploadData.gridSize}"
                    @input="${this._handleGridSizeInput}"
                  />
                  <small class="form-text text-muted">Typical values: 50-100 pixels per grid square</small>
                </div>

                ${
                  this._mapUploadData.imageFile
                    ? html`
                      <div class="mb-3">
                        <label class="form-label">Selected Image</label>
                        <div class="d-flex align-items-center">
                          <span class="text-muted small flex-grow-1">${this._mapUploadData.imageFile.name}</span>
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            @click="${this._clearMapImage}"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    `
                    : ''
                }
              </div>

              <!-- Right Panel - Map Preview with Grid -->
              <div
                class="flex-grow-1 border rounded d-flex align-items-center justify-content-center map-preview-container ${
                  this._isMapDragging ? 'border-primary bg-light' : ''
                }"
                @dragover="${this._handleMapDragOver}"
                @dragleave="${this._handleMapDragLeave}"
                @drop="${this._handleMapDrop}"
              >
                ${
                  this._mapImagePreview
                    ? html`
                      <div
                        style="position: relative; display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; cursor: ${
                          this._isPanning ? 'grabbing' : 'grab'
                        };"
                        @mousedown="${this._startPan}"
                        @mousemove="${this._doPan}"
                        @mouseup="${this._endPan}"
                        @mouseleave="${this._endPan}"
                      >
                        <canvas
                          id="mapPreviewCanvas"
                          style="transform: translate(${this._mapPan.x}px, ${this._mapPan.y}px) scale(${
                            this._mapZoom
                          }); transform-origin: center center; pointer-events: none;"
                        ></canvas>

                        <!-- Zoom Controls -->
                        <div class="zoom-controls d-flex flex-column gap-2">
                          <button
                            type="button"
                            class="btn btn-light btn-sm shadow"
                            title="Zoom In"
                            @click="${this._zoomIn}"
                          >
                            <i class="bi bi-zoom-in"></i>
                          </button>
                          <button
                            type="button"
                            class="btn btn-light btn-sm shadow"
                            title="Reset Zoom (100%)"
                            @click="${this._resetZoom}"
                          >
                            ${Math.round(this._mapZoom * 100)}%
                          </button>
                          <button
                            type="button"
                            class="btn btn-light btn-sm shadow"
                            title="Zoom Out"
                            @click="${this._zoomOut}"
                          >
                            <i class="bi bi-zoom-out"></i>
                          </button>
                        </div>
                      </div>
                    `
                    : html`
                      <div class="text-center p-5">
                        <i class="bi bi-cloud-upload fs-1 text-muted"></i>
                        <p class="mb-0 mt-3">Drag and drop map image here</p>
                        <small class="text-muted">Any image format</small>
                      </div>
                    `
                }
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="${this._closeUploadMapModal}">Cancel</button>
              <button
                type="button"
                class="btn btn-primary"
                ?disabled="${this._mapUploading}"
                @click="${this._uploadMap}"
              >
                ${
                  this._mapUploading
                    ? html`
                      <span class="spinner-border spinner-border-sm me-1"></span>
                      Uploading...
                    `
                    : 'Upload'
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _renderAddExitConditionModal() {
    return html`
      <div class="modal d-block modal-backdrop" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Add Exit Condition</h5>
              <button type="button" class="btn-close" @click="${this._closeAddExitConditionModal}"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label for="exitConditionMap" class="form-label">Map</label>
                <select
                  id="exitConditionMap"
                  class="form-select"
                  .value="${this._newExitCondition.mapId}"
                  @change="${this._handleConditionMapChange}"
                >
                  ${this._maps.map(
                    (map) => html`
                      <option value="${map.id}" ?selected="${map.id === this._newExitCondition.mapId}">
                        ${map.name}
                      </option>
                    `,
                  )}
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label">Condition Type</label>
                <div class="form-check">
                  <input
                    id="conditionTypeSlug"
                    type="radio"
                    class="form-check-input"
                    name="conditionType"
                    value="defeat-all-of-type"
                    ?checked="${this._newExitCondition.conditionType === 'defeat-all-of-type'}"
                    @change="${this._handleConditionTypeChange}"
                  />
                  <label class="form-check-label" for="conditionTypeSlug"> Defeat all of a token type </label>
                </div>
                <div class="form-check">
                  <input
                    id="conditionTypeToken"
                    type="radio"
                    class="form-check-input"
                    name="conditionType"
                    value="defeat-specific-token"
                    ?checked="${this._newExitCondition.conditionType === 'defeat-specific-token'}"
                    @change="${this._handleConditionTypeChange}"
                  />
                  <label class="form-check-label" for="conditionTypeToken"> Defeat a specific token </label>
                </div>
              </div>

              ${
                this._newExitCondition.conditionType === 'defeat-all-of-type'
                  ? html`
                    <div class="mb-3">
                      <label for="targetSlug" class="form-label">Token Type (Slug)</label>
                      <input
                        id="targetSlug"
                        type="text"
                        class="form-control"
                        placeholder="e.g., goblin, orc-warrior"
                        .value="${this._newExitCondition.targetSlug}"
                        @input="${this._handleTargetSlugInput}"
                      />
                      <small class="form-text text-muted">
                        Enter the slug of the token type to defeat (all tokens with this slug on the map).
                      </small>
                    </div>
                  `
                  : html`
                    <div class="mb-3">
                      <label for="targetTokenId" class="form-label">Token ID</label>
                      <input
                        id="targetTokenId"
                        type="text"
                        class="form-control"
                        placeholder="UUID of the specific token"
                        .value="${this._newExitCondition.targetTokenId}"
                        @input="${this._handleTargetTokenIdInput}"
                      />
                      <small class="form-text text-muted"> Enter the UUID of the specific token to defeat. </small>
                    </div>
                  `
              }
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="${this._closeAddExitConditionModal}">
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                ?disabled="${this._exitConditionSaving}"
                @click="${this._createExitCondition}"
              >
                ${
                  this._exitConditionSaving
                    ? html`
                      <span class="spinner-border spinner-border-sm me-1"></span>
                      Saving...
                    `
                    : 'Add Condition'
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('staff-adventure-form-view', StaffAdventureFormView);
